import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

export interface TTSVoice {
  name: string;
  lang: string;
  gender?: 'male' | 'female';
  voice: SpeechSynthesisVoice;
}

export interface TTSState {
  isSpeaking: boolean;
  isPaused: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorMessage: string | null;
  availableVoices: TTSVoice[];
  selectedVoice: TTSVoice | null;
  rate: number;
  pitch: number;
  volume: number;
  currentText: string;
}

export interface TTSControls {
  speak: (text: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVoice: (voice: TTSVoice) => void;
  setRate: (rate: number) => void;
  setPitch: (pitch: number) => void;
  setVolume: (volume: number) => void;
  getVoicesByGender: (gender: 'male' | 'female') => TTSVoice[];
  clearError: () => void;
}

export function useTextToSpeech() {
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const [state, setState] = useState<TTSState>({
    isSpeaking: false,
    isPaused: false,
    isLoading: true,
    hasError: false,
    errorMessage: null,
    availableVoices: [],
    selectedVoice: null,
    rate: 1.0,
    pitch: 1.0,
    volume: 1.0,
    currentText: '',
  });

  const categorizeVoice = (voice: SpeechSynthesisVoice): 'male' | 'female' | undefined => {
    const name = voice.name.toLowerCase();
    
    const maleKeywords = ['male', 'man', 'daniel', 'alex', 'james', 'thomas', 'ralph', 'fred'];
    const femaleKeywords = ['female', 'woman', 'samantha', 'karen', 'victoria', 'alice', 'moira', 'fiona', 'kate', 'susan'];
    
    if (maleKeywords.some(keyword => name.includes(keyword))) {
      return 'male';
    }
    if (femaleKeywords.some(keyword => name.includes(keyword))) {
      return 'female';
    }
    
    return undefined;
  };

  const loadVoices = useCallback((isRetry = false) => {
    const voices = window.speechSynthesis.getVoices();
    
    if (voices.length === 0) {
      // If no voices yet, schedule a retry after a short delay
      if (!isRetry) {
        setTimeout(() => loadVoices(true), 500);
      }
      return;
    }

    const categorizedVoices: TTSVoice[] = voices
      .filter(voice => voice.lang.startsWith('en'))
      .map(voice => ({
        name: voice.name,
        lang: voice.lang,
        gender: categorizeVoice(voice),
        voice,
      }));

    // If no English voices, use all available voices
    const finalVoices = categorizedVoices.length > 0 ? categorizedVoices : voices.map(voice => ({
      name: voice.name,
      lang: voice.lang,
      gender: categorizeVoice(voice),
      voice,
    }));

    setState(prev => ({
      ...prev,
      availableVoices: finalVoices,
      isLoading: false,
      selectedVoice: prev.selectedVoice || finalVoices.find(v => v.gender === 'female') || finalVoices[0] || null,
    }));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.error('Speech Synthesis API not supported');
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    loadVoices();

    const handleVoicesChanged = () => loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);

    // Fallback: Set isLoading to false after 2 seconds if voices haven't loaded
    // Some browsers don't fire voiceschanged event or have no voices available
    const timeout = setTimeout(() => {
      setState(prev => {
        if (prev.isLoading) {
          return { ...prev, isLoading: false };
        }
        return prev;
      });
    }, 2000);

    return () => {
      clearTimeout(timeout);
      window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
      if (utteranceRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, [loadVoices]);

  const speak = useCallback((text: string) => {
    if (!window.speechSynthesis) {
      console.error('Speech synthesis not supported');
      return;
    }

    // Get voice to use - prefer selectedVoice, fallback to any available voice
    let voiceToUse = state.selectedVoice?.voice;
    if (!voiceToUse) {
      // Try to get any available voice directly from speechSynthesis
      const allVoices = window.speechSynthesis.getVoices();
      const englishVoice = allVoices.find(v => v.lang.startsWith('en'));
      voiceToUse = englishVoice || allVoices[0];
    }

    if (!voiceToUse) {
      console.error('No voices available');
      return;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voiceToUse;
    utterance.rate = state.rate;
    utterance.pitch = state.pitch;
    utterance.volume = state.volume;

    utterance.onstart = () => {
      setState(prev => ({
        ...prev,
        isSpeaking: true,
        isPaused: false,
        currentText: text,
      }));
    };

    utterance.onend = () => {
      setState(prev => ({
        ...prev,
        isSpeaking: false,
        isPaused: false,
        currentText: '',
      }));
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      const errorMsg = event.error === 'not-allowed' 
        ? 'Speech synthesis is blocked. Please allow audio playback in your browser.'
        : event.error === 'audio-busy'
        ? 'Audio is busy. Please try again.'
        : event.error === 'network'
        ? 'Network error. Please check your connection.'
        : 'Text-to-speech failed. Try using "Record My Voice" instead.';
      
      setState(prev => ({
        ...prev,
        isSpeaking: false,
        isPaused: false,
        hasError: true,
        errorMessage: errorMsg,
      }));
    };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [state.selectedVoice, state.rate, state.pitch, state.volume]);

  const pause = useCallback(() => {
    if (window.speechSynthesis && state.isSpeaking) {
      window.speechSynthesis.pause();
      setState(prev => ({
        ...prev,
        isPaused: true,
      }));
    }
  }, [state.isSpeaking]);

  const resume = useCallback(() => {
    if (window.speechSynthesis && state.isPaused) {
      window.speechSynthesis.resume();
      setState(prev => ({
        ...prev,
        isPaused: false,
      }));
    }
  }, [state.isPaused]);

  const stop = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setState(prev => ({
        ...prev,
        isSpeaking: false,
        isPaused: false,
        currentText: '',
      }));
    }
  }, []);

  const setVoice = useCallback((voice: TTSVoice) => {
    setState(prev => ({
      ...prev,
      selectedVoice: voice,
    }));
  }, []);

  const setRate = useCallback((rate: number) => {
    setState(prev => ({
      ...prev,
      rate: Math.max(0.1, Math.min(10, rate)),
    }));
  }, []);

  const setPitch = useCallback((pitch: number) => {
    setState(prev => ({
      ...prev,
      pitch: Math.max(0, Math.min(2, pitch)),
    }));
  }, []);

  const setVolume = useCallback((volume: number) => {
    setState(prev => ({
      ...prev,
      volume: Math.max(0, Math.min(1, volume)),
    }));
  }, []);

  const getVoicesByGender = useCallback((gender: 'male' | 'female'): TTSVoice[] => {
    const filtered = state.availableVoices.filter(v => v.gender === gender);
    
    if (filtered.length === 0) {
      // If no gendered voices found, return alternating voices to provide variety
      // Use odd-indexed voices for male, even-indexed for female
      const alternateVoices = state.availableVoices.filter((_, idx) => 
        gender === 'male' ? idx % 2 === 1 : idx % 2 === 0
      );
      
      if (alternateVoices.length > 0) {
        return alternateVoices.slice(0, 1);
      }
      
      return state.availableVoices.slice(0, 1);
    }
    
    return filtered;
  }, [state.availableVoices]);

  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      hasError: false,
      errorMessage: null,
    }));
  }, []);

  const controls: TTSControls = useMemo(() => ({
    speak,
    pause,
    resume,
    stop,
    setVoice,
    setRate,
    setPitch,
    setVolume,
    getVoicesByGender,
    clearError,
  }), [speak, pause, resume, stop, setVoice, setRate, setPitch, setVolume, getVoicesByGender, clearError]);

  return { state, controls };
}
